import {AGENCY_KEYS} from "@/api/agencyApi";
import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {COMPANY_KEYS} from "@/api/companyApi";
import {stripParamKeys} from "@/api/influencerApi";
import Button from "@/components/Common/Button";
import type {
  GroupInfluencerListDetailSerializer,
  GroupListSerializer,
  GroupSerializer,
  InfluencerProfileDetailSerializer,
} from "@/generated/DjangoTypes";
import {useSliceInfluencerQuery} from "@/hooks/useInfluencer";
import {useGenericInfiniteQuery, useGenericInfiniteQueryWithCount, useGenericQuery} from "@/hooks/useQueryHooks";
import {useIsAgency, useSelectedCompanyId} from "@/hooks/useUserHooks";
import {extractCutoffFromPageParams, sumCountsByCutoffGroup} from "@/hooks/useVibeSearchBroadening";
import {client} from "@/httpClient";
import {i18n} from "@/i18n";
import {useManagementCampaign} from "@/store/campaignManagement";
import useTokenStore from "@/store/token";
import type {
  TInfiniteQueryOptions,
  TQueryOptions,
  TQueryResultWithCount,
  TVibePaginated,
  TVibeQueryResult,
} from "@/store/types";
import useUserStore from "@/store/user";
import {groupName} from "@/utils/groupUtils";
import {useInfiniteQuery, useMutation, useQueryClient} from "@tanstack/react-query";
import {useParams} from "@tanstack/react-router";
import type {AxiosError, AxiosResponse} from "axios";
import {toast, type Id} from "react-toastify";

export const GROUP_LIST_QUERY_CUSTOM_KEYS = ["company_profile", "group_selector", "influencer_network"] as const;

const CAMPAIGN_LISTS_WITH_GROUPS =
  /^\/campaign\/\d+\/(accepted_applications|all_invitable_influencers|cancelled_applications|declined_applications|favourite_influencers|influencers_following_you|invitable_pro_influencers|liked_campaigns|open_applications|pending_invitations|previously_worked_with|recommended_influencers|rejected_invitations|renegotiations)\//;

export const GROUP_KEYS = {
  mGroup: (groupId: number) => [`/group/${groupId}/`],
  mGroupCampaignInvite: (groupId: number) => [`/group/${groupId}/campaign_invite/`],
  mGroupCampaignInviteInfluencers: (groupId: number) => [`/group/${groupId}/add_invite_influencers/`],
  mGroupCampaignInviteInfluencersRemoval: (groupId: number) => [`/group/${groupId}/remove_invite_influencers/`],
  mGroupCampaignInviteRemoval: (groupId: number) => [`/group/${groupId}/remove_campaign_invite/`],
  mGroupList: ["/group/"],
  mGroupMember: (groupId: number) => [`/group/${groupId}/add_influencer/`],
  mGroupMemberRemoval: (groupId: number) => [`/group/${groupId}/remove_influencer/`],
  qGroup: (groupId: number) => [`/group/${groupId}/`],
  qGroupCsvData: (groupId: number) => [`/group/${groupId}/csv_data/`],
  qGroupInfluencers: (groupId: number, params = "") => [`/group/${groupId}/influencers/${params ? `?${params}` : ""}`],
  qGroupInfluencersCount: (groupId: number, params = "") => [
    `/group/${groupId}/influencers/?${params ? `${params}&` : ""}count_only=true`,
  ],
  qGroupList: (params?: string) => [`/group/?${params}`],
  qGroupNetworkData: (groupId: number) => [`/group/${groupId}/network_data/`],
  qGroupSimilarInfluencers: (groupId: number) => [`/group/${groupId}/similar_influencers/`],
} as const;

export const matchGroupInfluencers = (groupId: number) => ({
  predicate: (query: {queryKey: unknown}) =>
    Array.isArray(query.queryKey) &&
    typeof query.queryKey[0] === "string" &&
    query.queryKey[0].startsWith(`/group/${groupId}/influencers/`),
});

export const useGroupListQueryParams = ({
  agencyId,
  campaignId,
  companyId,
  excludeSmartGroups,
  searchParams = "",
  pageSize,
}: {
  agencyId?: number | null;
  campaignId?: number | null;
  companyId?: number | null;
  excludeSmartGroups?: boolean;
  searchParams?: string;
  pageSize?: number;
}) =>
  `${agencyId ? `agency_id=${agencyId}` : companyId ? `company_id=${companyId}` : ""}${campaignId ? `&campaign_id=${campaignId}` : ""}${searchParams ? `&${searchParams}` : ""}${pageSize ? `&page_size=${pageSize}` : ""}${excludeSmartGroups ? "&smart=false" : ""}`;

export const useRemoveInvitedGroupFromCampaignMutation = ({campaignId}: {campaignId: number}) => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: [`/campaign/${campaignId}/remove_campaign_invite/`],
    mutationFn: (groupId: number) =>
      client.post(GROUP_KEYS.mGroupCampaignInviteRemoval(groupId)[0], {campaign_id: campaignId}),
    onSuccess: () => {
      [CAMPAIGN_KEYS.qCampaignInvitedGroups(campaignId), CAMPAIGN_KEYS.qCampaignInvitations(campaignId)].map(queryKey =>
        queryClient.invalidateQueries({queryKey}),
      );
    },
  });
};

export const useUpdateInfluencerGroupMutation = ({
  action,
  group,
  influencer,
  onSuccessCallback,
  onUndoCallback,
  showToastOnSuccess = false,
}: {
  action: "add" | "remove";
  group?: GroupListSerializer | GroupSerializer | null;
  influencer: InfluencerProfileDetailSerializer;
  onSuccessCallback?: () => void;
  onUndoCallback?: () => void;
  showToastOnSuccess?: boolean;
}) => {
  const queryClient = useQueryClient();
  const isAgency = useIsAgency();
  const companyId = useSelectedCompanyId();
  const agencyId = isAgency && !companyId ? useUserStore.getState().user.agencies?.[0]?.agency?.id : undefined;
  let toastId: Id | null = null;

  const queryParams = useGroupListQueryParams({agencyId, companyId});
  const queryParamsNoSmart = useGroupListQueryParams({agencyId, companyId, excludeSmartGroups: true});

  const mutationKey =
    action === "add" ? GROUP_KEYS.mGroupMember(group?.id!) : GROUP_KEYS.mGroupMemberRemoval(group?.id!);

  return useMutation({
    meta: {skipSuccessToast: true},
    mutationKey,
    mutationFn: () => client.post(mutationKey[0], {influencer_id: influencer.uuid}),
    onSuccess: () => {
      if (showToastOnSuccess) {
        toastId = toast.info(
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-2">
              {influencer.picture320 ? (
                <img
                  src={influencer.picture320}
                  className="border-neutral-grey-light-dark size-8 shrink-0 rounded-full border"
                />
              ) : (
                <div className="border-neutral-grey-light-dark bg-primary-light-solid size-8 shrink-0 rounded-full border" />
              )}
              <div className="text-neutral-black flex flex-col gap-0.5 text-xs">
                {i18n.t(action === "add" ? "savedTo" : "removedFrom")}
                <span
                  onClick={() => window.open(`/group/${group?.id}`, "_blank")}
                  className="font-semibold hover:cursor-pointer hover:underline">
                  {groupName(group!)}
                </span>
              </div>
            </div>
            <Button
              onClick={() => {
                onUndoCallback?.();
                if (toastId) toast.dismiss(toastId);
              }}
              buttonStyle="White"
              className="h-auto px-8 py-2">
              {i18n.t("undo")}
            </Button>
          </div>,
          {
            className: "w-fit rounded-md px-4 py-2 bg-white",
          },
        );
      }

      onSuccessCallback?.();

      [
        {queryKey: GROUP_KEYS.qGroupList(queryParams)},
        {queryKey: GROUP_KEYS.qGroupList(queryParamsNoSmart)},
        {queryKey: GROUP_KEYS.qGroupSimilarInfluencers(group?.id!)},
        ...GROUP_LIST_QUERY_CUSTOM_KEYS.map(key => ({queryKey: [...GROUP_KEYS.qGroupList(queryParams), key]})),
        matchGroupInfluencers(group?.id!),
        {
          predicate: (query: {queryKey: readonly unknown[]}) =>
            typeof query.queryKey[0] === "string" && CAMPAIGN_LISTS_WITH_GROUPS.test(query.queryKey[0]),
        },
      ].map(filter => queryClient.invalidateQueries(filter));

      if (group?.name === "favourite" && companyId) {
        client.post(COMPANY_KEYS.mCompanyFavouriteInfluencer(companyId)[0], {influencer_id: influencer.uuid});
      }
    },
  });
};

export const useGroupQuery = ({
  groupId,
  queryOptions,
}: {
  groupId: number;
  queryOptions?: TQueryOptions<GroupSerializer>;
}) =>
  useGenericQuery<GroupSerializer>({
    queryKey: GROUP_KEYS.qGroup(groupId),
    enabled: !!groupId,
    ...queryOptions,
  });

type TGroupInfluencerPlatformCounts = {
  count: number;
  instagram_count: number;
  tiktok_count: number;
  ugc_count: number;
};

export const useGroupInfluencerPlatformCountsQuery = ({
  groupId,
  queryOptions,
  searchParams,
}: {
  groupId?: number;
  queryOptions?: TQueryOptions<TGroupInfluencerPlatformCounts>;
  searchParams?: string;
}) =>
  useGenericQuery<TGroupInfluencerPlatformCounts>({
    queryKey: GROUP_KEYS.qGroupInfluencersCount(groupId!, searchParams),
    enabled: !!groupId,
    ...queryOptions,
  });

export const useGroupInfluencersQuery = ({
  groupId,
  queryOptions,
  searchParams,
}: {
  groupId: number;
  queryOptions?: TInfiniteQueryOptions<
    GroupInfluencerListDetailSerializer,
    AxiosError,
    TVibeQueryResult<GroupInfluencerListDetailSerializer>
  >;
  searchParams?: string;
}) => {
  const {sliceInfluencers} = useSliceInfluencerQuery();
  const stableParams = searchParams ? stripParamKeys(searchParams) : searchParams;

  return useInfiniteQuery<
    AxiosResponse<TVibePaginated<GroupInfluencerListDetailSerializer>>,
    AxiosError,
    TVibeQueryResult<GroupInfluencerListDetailSerializer>
  >({
    queryKey: GROUP_KEYS.qGroupInfluencers(groupId, stableParams),
    initialPageParam: stableParams ? GROUP_KEYS.qGroupInfluencers(groupId, stableParams)[0] : undefined,
    getNextPageParam: lastPage => lastPage.data.next,
    enabled: !!groupId,
    select: response => {
      const firstPage = response.pages[0]?.data;
      return {
        count: sumCountsByCutoffGroup(response.pageParams, response.pages),
        cutoffValue: extractCutoffFromPageParams(response.pageParams),
        frequencyDistribution: firstPage?.frequency_distribution ?? {},
        noCutoff: !!firstPage?.no_cutoff,
        results: sliceInfluencers({
          hasNext: !!response.pages[response.pages.length - 1]?.data.next,
          influencers: response.pages.flatMap(page => page.data.results),
        }),
      };
    },
    ...queryOptions,
  });
};

export const useGroupListQuery = ({
  agencyId,
  campaignId,
  companyId: companyIdOverride,
  searchParams,
  queryOptions,
}: {
  agencyId?: number;
  campaignId?: number;
  companyId?: number;
  searchParams?: string;
  queryOptions?: TInfiniteQueryOptions<GroupListSerializer, AxiosError, TQueryResultWithCount<GroupListSerializer[]>>;
}) => {
  const {companyId: companyIdFromUrl} = useParams({strict: false});
  const managedCampaign = useManagementCampaign();
  const selectedCompanyId = useSelectedCompanyId();
  const companyId = companyIdOverride ?? companyIdFromUrl ?? selectedCompanyId ?? managedCampaign.company_profile?.id;

  return useGenericInfiniteQueryWithCount<GroupListSerializer>({
    queryKey: GROUP_KEYS.qGroupList(
      useGroupListQueryParams({agencyId, campaignId, companyId: companyId ? +companyId : undefined, searchParams}),
    ),
    enabled: !!useTokenStore.getState().userId,
    ...queryOptions,
  });
};

export const useRecommendedInfluencersQuery = ({
  agencyId,
  companyId,
  queryOptions,
}: {
  agencyId?: number;
  companyId?: number;
  queryOptions?: TInfiniteQueryOptions<GroupInfluencerListDetailSerializer>;
}) =>
  useGenericInfiniteQuery<GroupInfluencerListDetailSerializer>({
    queryKey:
      !!agencyId && !companyId
        ? AGENCY_KEYS.qAgencyRecommendedInfluencers(agencyId)
        : COMPANY_KEYS.qCompanyRecommendedInfluencers(companyId!),
    enabled: !!agencyId || !!companyId,
    ...queryOptions,
  });
